import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncMultiselect, AsyncSearch, Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useAuth } from 'contexts';
import useCustomerCarrierApi, { CustomerCarrier } from 'hooks/use-customer-carrier-api';
import { fetchPaginated, searchTruckingCompanies } from 'services';
import { Customer, Page, Role, TruckingCompany } from 'types';
import { isNotNull } from 'utils';
import { formatCustomer, formatTruckingCompany } from 'utils/format';

import LockableDateInput from 'components/inputs/date/lockable';
import dayjs from 'dayjs';
import './rule.scss';

const NewCustomerRule: FC = () => {
  const navigate = useNavigate();

  const { userProfile } = useAuth();
  const { createRule } = useCustomerCarrierApi();

  const [loading, setLoading] = useState<boolean>(false);

  const [selection, setSelection] = useState<Map<number, Customer | null>>(new Map());
  const customers: Customer[] = useMemo(() => [...selection.values()].filter(isNotNull), [selection]);
  const [truckingCompany, setTruckingCompany] = useState<TruckingCompany>();
  const [gateInAllowed, setGateInAllowed] = useState<boolean>(false);
  const [gateOutAllowed, setGateOutAllowed] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>();
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (userProfile.role === Role.ADMIN_CUSTOMER && userProfile.customers?.length === 1 && selection.size === 0) {
      const single = userProfile.customers[0];
      setSelection(new Map([[single.id, single]]));
    }
  }, [userProfile.role, userProfile.customers, selection.size]);

  const fetchCustomerData = useCallback(
    async (params: {
      pageNum?: number;
      pageSize?: number;
      from?: Date;
      to?: Date;
      sortField?: string;
      sortDirection?: string;
      filterString?: string;
    }): Promise<Page<Customer>> => {
      if (userProfile.role === Role.ADMIN_CUSTOMER) {
        const allowedCustomers = userProfile.customers ?? [];
        const filterStr = params.filterString ?? '';
        const match = filterStr.match(/name=="(.*)\*"/);
        const searchInput = match ? match[1].toLowerCase() : '';
        const filtered = searchInput
          ? allowedCustomers.filter(c => c.name?.toLowerCase().includes(searchInput))
          : allowedCustomers;
        return {
          content: filtered,
          totalElements: filtered.length,
          totalPages: 1
        };
      }
      return fetchPaginated<Customer>('customers/page')(params);
    },
    [userProfile.role, userProfile.customers]
  );

  const handleReset = useCallback(() => navigate('/scac-management/account-rules', { replace: true }), [navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      const responses: Array<CustomerCarrier | null> = [];
      const it = selection.values();
      let next = it.next();
      while (!next.done) {
        const customer = next.value;
        if (isNotNull(customer)) {
          responses.push(
            await createRule({
              customer,
              truckingCompany,
              gateInAllowed,
              gateOutAllowed,
              startDate,
              notes
            })
          );
        }
        next = it.next();
      }
      await Promise.allSettled(responses);
      if (responses.every(isNotNull)) {
        navigate('/scac-management/account-rules', { replace: true });
      }
      setLoading(false);
    },
    [selection, truckingCompany, gateInAllowed, gateOutAllowed, startDate, notes, createRule, navigate, setLoading]
  );

  return (
    <Loader loading={loading}>
      <ErrorDialog />

      <div id="scac-management" className="page">
        <div className="page-header">
          <h1>SCAC MANAGEMENT</h1>
        </div>
        <div className="page-content h-100">
          <form
            id="customer-carrier-rule"
            className="panel h-100"
            onReset={handleReset}
            onSubmit={handleSubmit}
            data-cy="account-rule-form"
          >
            <div className="d-flex align-items-center justify-content-between">
              <h4>ADD ACCOUNT RULES</h4>
              <div className="grid-2">
                <button className="btn-outline" type="reset">
                  CANCEL
                </button>
                <button className="btn-primary" type="submit">
                  SUBMIT
                </button>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header mb-5" />
              <div className="card-body grid-auto auto-rows">
                <div className={`d-flex flex-column g-2 row-span-${Math.max(4, 1 + customers.length)}`}>
                  <AsyncMultiselect<Customer>
                    id="rule-accounts"
                    label="ACCOUNT"
                    sortField="name"
                    fetchData={fetchCustomerData}
                    formatOption={formatCustomer}
                    handleSelect={setSelection}
                    rowSelection={selection}
                    required={!customers.length}
                    data-cy="account-search"
                  />
                  {customers.map(customer => {
                    return (
                      <div className="tag g-2 max-width-fit" key={customer.id}>
                        <p className="text-primary">{customer.name}</p>
                        <button
                          type="button"
                          className="tag-close"
                          onClick={() => setSelection(prevState => new Map(prevState).set(customer.id, null))}
                        >
                          <span className="material-icons">close</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <AsyncSearch<TruckingCompany>
                  required
                  label="CARRIER"
                  formatValue={formatTruckingCompany}
                  handleSelect={setTruckingCompany}
                  search={searchTruckingCompanies}
                  value={truckingCompany}
                  data-cy="carrier-search"
                />
                <LockableDateInput
                  date={startDate}
                  setDate={setStartDate}
                  label="START DATE"
                  min={dayjs().toISOString()}
                  data-cy="start-date-input"
                />
                <div className="text-field">
                  <input
                    className={`h-100 w-100 ${!notes ? 'blank' : ''}`}
                    id="notes-input"
                    name="notes"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    data-cy="notes-input"
                  />
                  <label htmlFor="notes-input">NOTES</label>
                </div>
                <div className="grid-2 mt-2">
                  <div className="text-field">
                    <input
                      id="gate-in-checkbox"
                      type="checkbox"
                      checked={gateInAllowed}
                      onChange={e => setGateInAllowed(e.target.checked)}
                      data-cy="allow-gate-in-checkbox"
                    />
                    <label htmlFor="gate-in-checkbox">ALLOW GATE IN</label>
                  </div>
                  <div className="text-field">
                    <input
                      id="gate-out-checkbox"
                      type="checkbox"
                      checked={gateOutAllowed}
                      onChange={e => setGateOutAllowed(e.target.checked)}
                      data-cy="allow-gate-out-checkbox"
                    />
                    <label htmlFor="gate-out-checkbox">ALLOW GATE OUT</label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Loader>
  );
};

export default NewCustomerRule;
